import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plane } from 'lucide-react';

// Phrase library with categories
const PHRASES = {
  serious: [
    { text: "Running a compliance pre-check…", highlight: "compliance pre-check" },
    { text: "Checking crate safety & comfort…", highlight: "safety & comfort" },
    { text: "Verifying door & fasteners…", highlight: "door & fasteners" },
    { text: "Scanning ventilation on all sides…", highlight: "ventilation" },
    { text: "Detecting common airline refusal risks…", highlight: "refusal risks" },
    { text: "Building your fix checklist…", highlight: "fix checklist" },
    { text: "Final consistency check…", highlight: "consistency check" },
  ],
  funLight: [
    { text: "Your crate is in the inspection zone ✈️", highlight: "inspection zone", style: "badge" },
    { text: "No stress — we're double-checking the details.", highlight: "double-checking", style: "badge" },
    { text: "One last scan to reduce surprise fees at the airport…", highlight: "surprise fees" },
    { text: "Almost there — precision mode ON.", highlight: "precision mode ON", style: "badge" },
    { text: "Tiny details matter. We're hunting them.", highlight: "Tiny details" },
    { text: "Helping your pet pass check-in smoothly 🐾", highlight: "pass check-in", style: "badge" },
  ],
  tips: [
    { text: "Quick tip: attach 2 empty bowls (water + food).", highlight: "2 empty bowls", style: "tip" },
    { text: "Quick tip: use a dry absorbent pad — no loose blankets.", highlight: "absorbent pad", style: "tip" },
    { text: "Quick tip: airlines often require sturdy door fasteners.", highlight: "sturdy door fasteners", style: "tip" },
  ],
  patience: [
    { text: "Still working — this can take a little longer on high-res photos.", highlight: "high-res photos" },
    { text: "Thanks for your patience. Final checks in progress.", highlight: "Final checks" },
    { text: "Thorough analysis takes a moment. Almost done!", highlight: "Almost done" },
  ]
};

// Shuffle and create phrase queue
const createPhraseQueue = () => {
  const allPhrases = [
    ...PHRASES.serious,
    ...PHRASES.serious,
    ...PHRASES.funLight,
    ...PHRASES.funLight,
    ...PHRASES.tips, // Tips appear less often
  ];
  
  // Fisher-Yates shuffle
  for (let i = allPhrases.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allPhrases[i], allPhrases[j]] = [allPhrases[j], allPhrases[i]];
  }
  
  return allPhrases;
};

// Radar sweep component
const RadarSweep = () => (
  <div className="absolute inset-0 overflow-hidden rounded-full">
    <motion.div
      className="absolute inset-0 origin-center"
      style={{
        background: 'conic-gradient(from 0deg, transparent 0deg, rgba(0, 102, 204, 0.15) 30deg, transparent 60deg)',
      }}
      animate={{ rotate: 360 }}
      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
    />
  </div>
);

// Main spinner component
const Spinner = ({ progress }) => (
  <div className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48">
    {/* Background circle */}
    <div className="absolute inset-0 rounded-full border-4 border-slate-100" />
    
    {/* Radar sweep effect */}
    <RadarSweep />
    
    {/* Progress ring */}
    <svg className="absolute inset-0 w-full h-full -rotate-90">
      <circle
        cx="50%"
        cy="50%"
        r="45%"
        fill="none"
        stroke="#E2E8F0"
        strokeWidth="4"
      />
      <motion.circle
        cx="50%"
        cy="50%"
        r="45%"
        fill="none"
        stroke="url(#progressGradient)"
        strokeWidth="4"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: progress / 100 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        style={{
          strokeDasharray: "283",
          strokeDashoffset: "0",
        }}
      />
      <defs>
        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0066CC" />
          <stop offset="50%" stopColor="#22C55E" />
          <stop offset="100%" stopColor="#0066CC" />
        </linearGradient>
      </defs>
    </svg>
    
    {/* Center icon */}
    <div className="absolute inset-0 flex items-center justify-center">
      <motion.div
        animate={{ 
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0]
        }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#0066CC] to-[#0052A3] rounded-full flex items-center justify-center shadow-lg shadow-blue-200"
      >
        <Plane className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
      </motion.div>
    </div>
    
    {/* Orbiting dots */}
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className="absolute w-3 h-3 rounded-full bg-[#0066CC]"
        style={{
          top: '50%',
          left: '50%',
          marginTop: '-6px',
          marginLeft: '-6px',
        }}
        animate={{
          x: [0, 60, 0, -60, 0].map(v => v * Math.cos(i * (2 * Math.PI / 3))),
          y: [60, 0, -60, 0, 60].map(v => v * Math.sin(i * (2 * Math.PI / 3) + Math.PI / 2)),
          opacity: [0.3, 1, 0.3, 1, 0.3],
          scale: [0.5, 1, 0.5, 1, 0.5],
        }}
        transition={{
          duration: 4,
          delay: i * 0.3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    ))}
  </div>
);

// Phrase display component with animations
const AnimatedPhrase = ({ phrase, styleType }) => {
  const renderHighlightedText = () => {
    if (!phrase.highlight) return phrase.text;
    
    const parts = phrase.text.split(phrase.highlight);
    const isBadge = phrase.style === 'badge' || styleType === 'badge';
    const isTip = phrase.style === 'tip';
    
    return (
      <>
        {parts[0]}
        {isBadge ? (
          <span className="relative inline-block mx-1">
            <span className="relative z-10 px-3 py-1 text-white font-semibold">
              {phrase.highlight}
            </span>
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-[#0066CC] to-[#22C55E] rounded-full"
              animate={{ 
                boxShadow: [
                  '0 0 10px rgba(0, 102, 204, 0.3)',
                  '0 0 20px rgba(0, 102, 204, 0.5)',
                  '0 0 10px rgba(0, 102, 204, 0.3)'
                ]
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </span>
        ) : isTip ? (
          <span className="relative inline-block">
            <span className="text-amber-600 font-semibold">{phrase.highlight}</span>
            <motion.span
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-400"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </span>
        ) : (
          <span className="relative inline-block">
            <span className="text-[#0066CC] font-semibold">{phrase.highlight}</span>
            <motion.span
              className="absolute -inset-1 bg-blue-100 rounded-lg -z-10"
              animate={{ 
                opacity: [0.5, 0.8, 0.5],
                scale: [1, 1.05, 1]
              }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
            <motion.span
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#0066CC] via-[#22C55E] to-[#0066CC]"
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ backgroundSize: '200% 100%' }}
            />
          </span>
        )}
        {parts[1]}
      </>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        duration: 0.4,
        ease: "easeOut"
      }}
      className="text-center px-4"
    >
      <p className="text-lg sm:text-xl md:text-2xl text-slate-700 leading-relaxed">
        {renderHighlightedText()}
      </p>
    </motion.div>
  );
};

// Main LoadingExperience component
const LoadingExperience = ({ 
  isVisible = true, 
  estimatedDuration = 45,
  onComplete 
}) => {
  const [progress, setProgress] = useState(0);
  const [currentPhrase, setCurrentPhrase] = useState(null);
  const [phraseQueue, setPhraseQueue] = useState([]);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [styleType, setStyleType] = useState('glow');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isExtended, setIsExtended] = useState(false);

  // Initialize phrase queue
  useEffect(() => {
    if (isVisible) {
      setPhraseQueue(createPhraseQueue());
      setPhraseIndex(0);
      setProgress(0);
      setElapsedTime(0);
      setIsExtended(false);
    }
  }, [isVisible]);

  // Progress simulation
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
      
      setProgress(prev => {
        if (prev >= 95) return 95; // Never reach 100 until actually complete
        
        // Slower progress as we get closer to estimated duration
        const remaining = estimatedDuration - (prev / 100 * estimatedDuration);
        const increment = Math.max(0.5, (remaining / estimatedDuration) * 3);
        
        return Math.min(95, prev + increment);
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isVisible, estimatedDuration]);

  // Check for extended loading
  useEffect(() => {
    if (elapsedTime > estimatedDuration && !isExtended) {
      setIsExtended(true);
    }
  }, [elapsedTime, estimatedDuration, isExtended]);

  // Phrase rotation
  useEffect(() => {
    if (!isVisible || phraseQueue.length === 0) return;

    const getNextPhrase = () => {
      if (isExtended) {
        // Use patience phrases when loading takes longer
        const patiencePhrases = PHRASES.patience;
        return patiencePhrases[Math.floor(Math.random() * patiencePhrases.length)];
      }
      
      const nextIndex = (phraseIndex + 1) % phraseQueue.length;
      setPhraseIndex(nextIndex);
      return phraseQueue[nextIndex];
    };

    // Initial phrase
    setCurrentPhrase(phraseQueue[0]);
    setStyleType(Math.random() > 0.5 ? 'glow' : 'badge');

    // Rotate phrases
    const rotationInterval = setInterval(() => {
      setCurrentPhrase(getNextPhrase());
      setStyleType(prev => prev === 'glow' ? 'badge' : 'glow');
    }, 2200 + Math.random() * 800); // 2.2-3.0 seconds

    return () => clearInterval(rotationInterval);
  }, [isVisible, phraseQueue, phraseIndex, isExtended]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-4"
    >
      {/* Logo top right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <img 
          src="/logo.png" 
          alt="PetFlyCheck" 
          className="w-12 h-12 sm:w-16 sm:h-16 object-contain opacity-80"
        />
      </div>

      {/* Main content */}
      <div className="flex flex-col items-center max-w-2xl mx-auto">
        {/* Spinner */}
        <Spinner progress={progress} />
        
        {/* Progress text */}
        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Analysis in progress
          </p>
          <p className="text-slate-500 text-sm sm:text-base">
            Estimated time: 15–45 seconds
          </p>
          
          {/* Progress bar */}
          <div className="mt-4 w-64 sm:w-80 h-2 bg-slate-100 rounded-full overflow-hidden mx-auto">
            <motion.div
              className="h-full bg-gradient-to-r from-[#0066CC] via-[#22C55E] to-[#0066CC]"
              style={{ backgroundSize: '200% 100%' }}
              animate={{ 
                width: `${progress}%`,
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }}
              transition={{ 
                width: { duration: 0.5 },
                backgroundPosition: { duration: 3, repeat: Infinity }
              }}
            />
          </div>
          
          <p className="mt-2 text-xs sm:text-sm text-slate-400">
            {Math.round(progress)}% complete
          </p>
        </motion.div>

        {/* Animated phrase */}
        <div className="mt-10 sm:mt-12 h-20 sm:h-24 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {currentPhrase && (
              <AnimatedPhrase 
                key={currentPhrase.text} 
                phrase={currentPhrase}
                styleType={styleType}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Don't close message */}
        <motion.p
          className="mt-8 sm:mt-10 text-sm text-slate-400 text-center"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Almost done — please don't close this page.
        </motion.p>
      </div>

      {/* Decorative elements */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#0066CC] to-transparent opacity-20" />
      
      {/* Floating particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full bg-[#0066CC] opacity-20"
          style={{
            left: `${15 + i * 15}%`,
            bottom: '10%',
          }}
          animate={{
            y: [-20, -60, -20],
            opacity: [0.1, 0.3, 0.1],
            scale: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 3 + i * 0.5,
            delay: i * 0.3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </motion.div>
  );
};

export default LoadingExperience;
